import { useEffect, useState, useRef } from 'react';
import { useAuth } from '@/context';
import { syncProjects, syncDesigns } from '@/services/supabaseSync';
import { migrateDesignsToUser, migrateProjectsToUser } from '@/services/storage';
import { isSupabaseConfigured } from '@/config/supabase';

/**
 * Background cloud sync hook
 * Syncs projects and designs on app launch + every 5 minutes when authenticated
 * Includes auto-migration of existing local projects/designs on first sign-in
 *
 * Migration flow:
 * 1. User creates designs locally (no userId)
 * 2. User signs in
 * 3. This hook detects orphan designs/projects without userId
 * 4. Assigns current user's ID to those items
 * 5. Normal sync uploads them to cloud
 *
 * Usage: Call once at app root (App.tsx)
 */
export function useCloudSync() {
  const { user } = useAuth();
  const [hasMigrated, setHasMigrated] = useState(false);
  const migrationInProgressRef = useRef(false);

  useEffect(() => {
    if (!user || !isSupabaseConfigured()) return;

    const performSync = async () => {
      try {
        // First-time migration: Assign userId to orphan designs/projects
        // This runs once per sign-in session before the first sync
        if (!hasMigrated && !migrationInProgressRef.current) {
          migrationInProgressRef.current = true;

          const [migratedDesigns, migratedProjects] = await Promise.all([
            migrateDesignsToUser(user.id),
            migrateProjectsToUser(user.id),
          ]);

          if (migratedDesigns > 0 || migratedProjects > 0) {
            console.warn('[CloudSync] Migrated orphan data to user:', {
              designs: migratedDesigns,
              projects: migratedProjects,
            });
          }

          setHasMigrated(true);
          migrationInProgressRef.current = false;
        }

        // Now perform the actual sync (upload migrated items to cloud)
        const [projectsResult, designsResult] = await Promise.all([
          syncProjects(user.id),
          syncDesigns(user.id),
        ]);

        console.warn('[CloudSync] Complete:', {
          projects: projectsResult,
          designs: designsResult,
        });
      } catch (error) {
        console.error('[CloudSync] Failed:', error);
        migrationInProgressRef.current = false;
      }
    };

    // Initial sync on app launch
    performSync();

    // Background sync every 5 minutes
    const intervalId = setInterval(performSync, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [user, hasMigrated]);

  return null; // Silent hook, no return value needed
}
